import { TARGET_GROUP_NAME_MAX_LENGTH } from '@hprofits/api/target-group';
import { useCopyTargetGroup } from '@hprofits/api-client';
import { SUPPRESS_ERROR_TOAST_META } from '@hprofits/api-client/error-handling';
import { CampaignGroupId, TargetGroupId } from '@hprofits/shared';
import { MaybeRefOrGetter, Ref, ref, toValue } from 'vue';
import { useRouter } from 'vue-router';

import { apiClient } from '@/api-client';
import { t } from '@/i18n';
import { nextNewName } from '@/modules/campaigns/components/Layout/composables';
import { justCreatedState } from '@/modules/campaigns/composables/useOnJustCreated';
import {
  getTargetGroupCopyGenericErrorMessage,
  getTargetGroupCopyMutationErrorOverride,
} from '@/modules/campaigns/utils/mutation-errors/targetGroup';
import { useRouteResolverCampaigns } from '@/modules/common/route/useRouteResolverCampaigns';
import type { UsePageControllerResult } from '@/modules/common/state/usePageController';
import { createMutationErrorHandler } from '@/modules/shared/composables';

export type TargetGroupCopySource = Readonly<{
  id: TargetGroupId;
  name: string;
}>;

export type UseCopyTargetGroupActionProps = Readonly<{
  campaignGroupId: CampaignGroupId;
  targetGroupNames: MaybeRefOrGetter<ReadonlyArray<string>>;
  pageController: UsePageControllerResult;
}>;

export type UseCopyTargetGroupActionResult = Readonly<{
  handleCopy: (source: TargetGroupCopySource) => Promise<void>;
  /** Id of the target group being copied, or null — drives the per-row spinner. */
  copyingTargetGroupId: Readonly<Ref<TargetGroupId | null>>;
}>;

export const useCopyTargetGroupAction = ({
  campaignGroupId,
  targetGroupNames,
  pageController,
}: UseCopyTargetGroupActionProps): UseCopyTargetGroupActionResult => {
  const router = useRouter();
  const routeResolver = useRouteResolverCampaigns();
  const mutation = useCopyTargetGroup(apiClient, SUPPRESS_ERROR_TOAST_META);
  // The mutation's own isPending cannot tell the sidebar WHICH row is busy.
  const copyingTargetGroupId = ref<TargetGroupId | null>(null);

  const handleCopyError = createMutationErrorHandler({
    errors: pageController.errors,
    title: () => t('modules.campaigns.actions.errors.copyTargetGroup'),
    override: getTargetGroupCopyMutationErrorOverride,
    genericMessage: getTargetGroupCopyGenericErrorMessage,
    logTag: '[useCopyTargetGroupAction]',
  });

  const handleCopy = async (source: TargetGroupCopySource): Promise<void> => {
    if (copyingTargetGroupId.value !== null) {
      return;
    }
    copyingTargetGroupId.value = source.id;
    try {
      const { id } = await mutation.mutateAsync({
        campaignId: campaignGroupId,
        body: {
          sourceId: source.id,
          // Same generator as create, seeded with the source name, so a copy of
          // "Germany desktop" becomes "Germany desktop 1". Capped because the seed is
          // user-authored and may already sit at the limit.
          name: nextNewName(source.name, toValue(targetGroupNames), TARGET_GROUP_NAME_MAX_LENGTH),
        },
      });
      const targetGroupRoute = routeResolver.targetGroup(campaignGroupId, id);
      await router.push({
        ...targetGroupRoute,
        state: justCreatedState(String(id)),
      });
    } catch (err) {
      handleCopyError(err);
      // Error is surfaced on the page's ErrorsPanel; no rethrow needed.
    } finally {
      copyingTargetGroupId.value = null;
    }
  };

  return {
    handleCopy,
    copyingTargetGroupId,
  };
};
